import { ProductPricing } from '@galaxy/billing';
import { Environment } from '@galaxy/core';
import { AppPrices } from '@galaxy/marketplace-apps';
import {
  AppAndAddonActivationStatus,
  ListAppsAndAddonsActivationsStatusesForBusinessResponseAppsAndAddonsActivationStatusesInterface as ActivationStatusesInterface,
} from '@vendasta/accounts/legacy';
import { ResellerItem } from '@vendasta/store';
import { Activation } from '../../core/account.service';
import { ActivationResult, Order } from '../core/activate-app/interface';
import {
  activatableItemArrayToMap,
  combineArrays,
  convertActivatableItemsIntoOrders,
  convertResellerItemOrderFormToOrders,
} from '../core/activate-app/utils';
import {
  ActivatableItem,
  AppIdToEditionIdToEditionMap,
  Edition,
  isValidEditionId,
  ProductEditionMap,
  QuantityEditionIdPair,
  SelectedItems,
} from '../core/interface';
import { isAppActive } from '../core/utils';
import { ResellerItemOrderForm } from '../order-form-page';
import { ActivationError, OrderConfirmationItem } from './interface';

export function convertResellerItemsToSummaries(
  selectedItems: SelectedItems,
  resellerItemMap: Map<string, ResellerItem>,
  appPriceMap: Map<string, AppPrices>,
  partnerCurrency: string,
  env: Environment,
  retailProductPricingMap: Map<string, ProductPricing>,
  marketCurrency: string,
): OrderConfirmationItem[] {
  if (!selectedItems || !resellerItemMap) {
    return [];
  }
  return Array.from(selectedItems.keys()).reduce((acc, key) => {
    const quantityEditionPair = selectedItems.get(key);
    let quantity = 0;
    let editionId = null;
    if (quantityEditionPair) {
      quantity = quantityEditionPair.quantity;
      editionId = isValidEditionId(quantityEditionPair.editionId) ? quantityEditionPair.editionId : null;
    }

    const ri = resellerItemMap.get(key);
    if (!ri) {
      return acc;
    }

    const sku = editionId ? `${ri.billingId}:${editionId}` : ri.billingId;
    const productPricing = retailProductPricingMap ? retailProductPricingMap.get(sku) : undefined;
    const activationMessage = ri.orderForm ? ri.orderForm.activationMessage : undefined;

    const orderConfirmationItem = {
      name: ri.name,
      appId: ri.appId,
      addonId: ri.addonId,
      icon: ri.icon,
      quantity: quantity,
      activationMessage: activationMessage,
      requiresApproval: ri.activationRequiresApproval,
      editionId: editionId,
      tagline: ri.tagline,
      appPrices: appPriceMap.get(ri.addonId || ri.appId),
      partnerCurrency: partnerCurrency,
      retailTotal: (productPricing?.pricingRules[0]?.price ?? 0) * (quantity ?? 0),
      productPricing: productPricing,
      marketCurrency: marketCurrency,
    } as OrderConfirmationItem;

    acc.push(orderConfirmationItem);
    return acc;
  }, []);
}

export function filterOutActiveSelectedItems(
  selectedItems: SelectedItems,
  appStatuses: ActivationStatusesInterface[],
  resellerItemMap: Map<string, ResellerItem>,
  activations: Activation[],
): SelectedItems {
  return Array.from(selectedItems.keys()).reduce((m, appId) => {
    let isTrial = false;
    if (activations) {
      const activation = activations.find((act) => act.appId === appId);
      isTrial = activation && activation.trial;
    }
    const isActive = isAppActive(appId, appStatuses, isTrial);
    const allowMultiActivate = resellerItemMap.get(appId) && resellerItemMap.get(appId).allowMultipleActivations;
    if (!isActive || allowMultiActivate) {
      m.set(appId, selectedItems.get(appId));
    }
    return m;
  }, new Map<string, QuantityEditionIdPair>() as SelectedItems);
}

/** Given a list of product names, will shorthand them
 *  For example, if given ['app1', 'app2', 'app1'] will return ['app1 (2)', 'app2']
 * @param {string[]} names: array of string names
 * @returns {string[]}: shorthand names
 */
export function combineNames(names: string[]): string[] {
  const resultMap: Map<string, number> = new Map<string, number>();
  const outputNames: string[] = [];
  names.forEach((name) => {
    resultMap.set(name, (resultMap.get(name) || 0) + 1);
  });

  resultMap.forEach((num, name) => {
    const quantity = resultMap.get(name);
    const countString = num !== 1 ? `( ${quantity} )` : '';
    outputNames.push(`${name} ${countString}`);
  });
  return outputNames;
}

export function getFailedActivationErrorMessage(failedActivations: ActivationResult[]): ActivationError | undefined {
  if (!failedActivations || failedActivations.length === 0) {
    return;
  }
  const failedActivationNames = failedActivations.map((a) => a.name);
  let errorMessage = '';
  failedActivations.forEach((a) => {
    if (a.errorMessage) {
      errorMessage = errorMessage ? `${errorMessage}\n\t${a.errorMessage}` : a.errorMessage;
    }
  });
  const itemNames = combineNames(failedActivationNames);
  return {
    appNames: itemNames,
    errorMessage: errorMessage,
    type: 'activationFailure',
    openFailureDialog: true,
  } as ActivationError;
}

export function filterStringMap<T>(m: Map<string, T>, filterStrings: string[]): Map<string, T> {
  if (!m) {
    return new Map<string, T>();
  }
  return Array.from(m.entries()).reduce((thisMap, [id, value]) => {
    if (filterStrings.indexOf(id) < 0) {
      thisMap.set(id, value);
      return thisMap;
    }
  }, new Map<string, T>());
}

export function getAppsThatCantBeReactivated(
  appStatuses: ActivationStatusesInterface[],
  activatableItemMap: Map<string, ActivatableItem>,
): string[] {
  const activeStatues = [
    AppAndAddonActivationStatus.ACTIVATED,
    AppAndAddonActivationStatus.CANCELED,
    AppAndAddonActivationStatus.PENDING,
  ];
  return (appStatuses || []).reduce((arr, status) => {
    const alreadyActive = activeStatues.indexOf(status.status) >= 0;
    const activatableItem = activatableItemMap.get(status.appId);
    if (!activatableItem) {
      arr.push(status.appId);
    } else if (alreadyActive && !activatableItem.allowMultipleActivations && !activatableItem.isTrial) {
      arr.push(status.appId);
    }
    return arr;
  }, []);
}

export function convertEverythingIntoOrders(
  businessId: string,
  appForms: ResellerItemOrderForm[],
  addonForms: ResellerItemOrderForm[],
  selectedItems: SelectedItems,
  activatableItems: ActivatableItem[],
  appStatuses: ActivationStatusesInterface[],
): Order[] {
  const activatableItemMap = activatableItemArrayToMap(activatableItems);
  const appsThatCantBeReactivated = getAppsThatCantBeReactivated(appStatuses, activatableItemMap);
  const selectedItemsThatCanBeActivated = filterStringMap(selectedItems, appsThatCantBeReactivated);

  const allForms = combineArrays(appForms, addonForms);
  const ordersWithForms = convertResellerItemOrderFormToOrders(businessId, allForms, selectedItemsThatCanBeActivated);

  const ordersWithFormIds = (ordersWithForms || []).map((order) => order.appId);
  const filteredSelectedItems = filterStringMap(selectedItemsThatCanBeActivated, ordersWithFormIds);

  const ordersWithoutForms = convertActivatableItemsIntoOrders(businessId, activatableItems, filteredSelectedItems);
  return combineArrays(ordersWithForms, ordersWithoutForms);
}

export function convertAppIdEditionsMapToEditionIdEditionMap(
  appIdEditionsMap: ProductEditionMap,
): AppIdToEditionIdToEditionMap {
  const appIdToEditionIdToEditionMap = new Map<string, Map<string, Edition>>();
  if (!appIdEditionsMap) {
    return appIdToEditionIdToEditionMap;
  }
  appIdEditionsMap.forEach((editions, appId) => {
    const editionIdToEditionMap = new Map<string, Edition>();
    editions.forEach((edition) => editionIdToEditionMap.set(edition.editionId, edition));
    appIdToEditionIdToEditionMap.set(appId, editionIdToEditionMap);
  });
  return appIdToEditionIdToEditionMap;
}
